import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ArrowRight,
  AlertTriangle,
  Phone,
  Wind,
  Heart,
  Brain,
  Activity,
  Smile,
  Search,
  ClipboardList,
  Eye,
  Stethoscope,
  RotateCcw,
  Gauge,
  Zap,
} from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// Site palette (aligned with CabinetHomePage)
const C = {
  primary: '#0EA5E9',
  primaryDark: '#0284C7',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
  cloud: '#F8FAFC',
  skyPale: '#F0F9FF',
  skySoft: '#E0F2FE',
  border: '#E2E8F0',
  white: '#FFFFFF',
  danger: '#DC2626',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FCA5A5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  amberBorder: '#FCD34D',
};

// --- Nijmegen questionnaire ------------------------------------------------
const NIJMEGEN_ITEMS = [
  'Douleur thoracique',
  'Sensation de tension',
  'Vision floue',
  'Vertiges',
  'Sensation de confusion',
  'Respiration rapide ou profonde',
  'Essoufflement',
  'Oppression thoracique',
  'Ventre gonflé',
  'Fourmillements dans les doigts',
  'Impossible de respirer à fond',
  'Doigts ou bras raides',
  'Tension autour de la bouche',
  'Mains ou pieds froids',
  'Palpitations',
  'Sensation d’anxiété',
];
const NIJMEGEN_LEVELS = [
  { value: 0, label: 'Jamais' },
  { value: 1, label: 'Rarement' },
  { value: 2, label: 'Parfois' },
  { value: 3, label: 'Souvent' },
  { value: 4, label: 'Très souvent' },
];

// --- Small building blocks -------------------------------------------------
const SectionLabel = ({ number, children }) => (
  <div className="flex items-center gap-3 mb-4">
    {number != null && (
      <span
        className="inline-flex items-center justify-center rounded-full text-xs font-semibold"
        style={{
          width: 28,
          height: 28,
          backgroundColor: C.skySoft,
          color: C.primaryDark,
          fontFamily: 'Figtree, sans-serif',
        }}
      >
        {number}
      </span>
    )}
    <span
      className="text-xs sm:text-sm uppercase tracking-[0.22em]"
      style={{ color: C.primaryDark, fontWeight: 500 }}
    >
      {children}
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2
    className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6"
    style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
  >
    {children}
  </h2>
);

const Callout = ({ variant = 'info', icon: Icon, title, children }) => {
  const styles =
    variant === 'danger'
      ? { bg: C.dangerBg, border: C.dangerBorder, color: C.danger }
      : variant === 'warning'
      ? { bg: C.amberBg, border: C.amberBorder, color: C.amber }
      : { bg: C.skyPale, border: C.skySoft, color: C.primaryDark };
  return (
    <div
      className="rounded-2xl p-5 sm:p-6 flex gap-4"
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
    >
      {Icon && (
        <div className="flex-shrink-0" style={{ color: styles.color }}>
          <Icon size={22} />
        </div>
      )}
      <div className="flex-1">
        {title && (
          <p className="mb-1 font-semibold" style={{ color: styles.color }}>
            {title}
          </p>
        )}
        <div style={{ color: C.ink, fontSize: '0.95rem', lineHeight: 1.6 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SymptomGroup = ({ icon: Icon, title, items }) => (
  <div
    className="rounded-2xl p-6 h-full"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    data-testid={`symptom-group-${title}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
      >
        <Icon size={20} />
      </div>
      <h3 className="text-lg font-medium" style={{ color: C.ink }}>
        {title}
      </h3>
    </div>
    <ul className="space-y-2">
      {items.map((s) => (
        <li key={s} className="flex gap-2 text-sm" style={{ color: C.slate }}>
          <span
            className="mt-2 rounded-full flex-shrink-0"
            style={{ width: 5, height: 5, backgroundColor: C.primary }}
          />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  </div>
);

const StepCard = ({ number, title, children }) => (
  <div
    className="rounded-2xl p-6 h-full flex flex-col"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
  >
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center mb-4"
      style={{
        backgroundColor: C.primary,
        color: C.white,
        fontFamily: 'Outfit, sans-serif',
        fontWeight: 500,
      }}
    >
      {number}
    </div>
    <h4 className="text-base font-semibold mb-2" style={{ color: C.ink }}>
      {title}
    </h4>
    <div className="text-sm leading-relaxed" style={{ color: C.slate }}>
      {children}
    </div>
  </div>
);

const ExerciseCard = ({ index, title, duration, steps, closing }) => (
  <div
    className="rounded-2xl p-6 sm:p-7 h-full"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    data-testid={`exercise-card-${index}`}
  >
    <div className="flex items-center justify-between mb-4">
      <span
        className="text-xs uppercase tracking-[0.2em] font-medium"
        style={{ color: C.primaryDark }}
      >
        Exercice {index}
      </span>
      <span
        className="text-xs px-3 py-1 rounded-full"
        style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
      >
        {duration}
      </span>
    </div>
    <h3
      className="text-xl mb-4"
      style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
    >
      {title}
    </h3>
    <ol className="space-y-3 mb-4">
      {steps.map((step, idx) => (
        <li key={idx} className="flex gap-3 text-sm" style={{ color: C.slate }}>
          <span
            className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: C.skyPale,
              color: C.primaryDark,
            }}
          >
            {idx + 1}
          </span>
          <span className="leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
    {closing && (
      <p
        className="text-sm italic pt-3"
        style={{ color: C.primaryDark, borderTop: `1px dashed ${C.border}` }}
      >
        {closing}
      </p>
    )}
  </div>
);

const FaqItem = ({ q, children, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
        style={{ color: C.ink }}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-medium">{q}</span>
        <ChevronRight
          size={18}
          style={{
            color: C.primary,
            transition: 'transform 0.25s ease',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 text-sm leading-relaxed"
          style={{ color: C.slate, borderTop: `1px solid ${C.border}` }}
        >
          <div className="pt-4 space-y-3">{children}</div>
        </div>
      )}
    </div>
  );
};

// --- Main page -------------------------------------------------------------
const HyperventilationPage = () => {
  const navigate = useNavigate();

  // Nijmegen
  const [answers, setAnswers] = useState(Array(NIJMEGEN_ITEMS.length).fill(null));
  const answeredCount = answers.filter((v) => v !== null).length;
  const totalScore = useMemo(
    () => answers.reduce((sum, v) => sum + (v ?? 0), 0),
    [answers]
  );
  const highFreqCount = useMemo(
    () => answers.filter((v) => v !== null && v >= 3).length,
    [answers]
  );
  const setAnswer = (i, v) =>
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  const resetAnswers = () => setAnswers(Array(NIJMEGEN_ITEMS.length).fill(null));

  const scoreInterpretation = () => {
    if (answeredCount === 0)
      return { color: C.muted, label: 'Répondez aux questions pour obtenir votre score.' };
    if (totalScore > 23)
      return {
        color: C.amber,
        label:
          'Score supérieur à 23 : suggère un syndrome d’hyperventilation. Un bilan est recommandé.',
      };
    if (highFreqCount >= 3)
      return {
        color: C.primaryDark,
        label:
          'Au moins 3 symptômes cotés « souvent » ou « très souvent » : oriente vers un trouble du schéma respiratoire.',
      };
    return {
      color: C.primary,
      label:
        'Score inférieur au seuil : profil peu évocateur d’hyperventilation, mais un avis reste utile en cas de gêne.',
    };
  };
  const scoreState = scoreInterpretation();

  // BOLT calculator
  const [boltInput, setBoltInput] = useState('');
  const boltValue = parseFloat(boltInput);
  const boltInterpretation = () => {
    if (isNaN(boltValue) || boltValue <= 0)
      return { color: C.muted, label: 'Entrez votre temps mesuré (en secondes) pour interpréter.' };
    if (boltValue < 10)
      return {
        color: C.danger,
        label:
          'Moins de 10 s — Respiration probablement bruyante, irrégulière ou excessive ; marge d’amélioration importante.',
      };
    if (boltValue < 20)
      return {
        color: C.amber,
        label:
          '10-20 s — Fondations respiratoires à développer ; congestion nasale, mauvais sommeil ou respiration buccale possibles.',
      };
    if (boltValue < 30)
      return {
        color: C.primaryDark,
        label:
          '20-30 s — Efficacité respiratoire correcte, avec potentiel d’amélioration.',
      };
    if (boltValue < 40)
      return {
        color: C.primary,
        label: '30-40 s — Très bonne tolérance au CO₂ et respiration efficace.',
      };
    return {
      color: C.primary,
      label: '40 s et plus — Niveau de référence présenté par la méthode.',
    };
  };
  const boltState = boltInterpretation();

  const symptomGroups = [
    {
      icon: Wind,
      title: 'Respiration',
      items: [
        'Essoufflement, impression de ne pas « prendre assez d’air »',
        'Soupirs et bâillements fréquents',
        'Respiration rapide, irrégulière ou par la bouche',
        'Impossibilité d’inspirer « à fond »',
        'Gêne respiratoire au repos comme à l’effort',
      ],
    },
    {
      icon: Heart,
      title: 'Thorax et cœur',
      items: [
        'Oppression ou douleur thoracique',
        'Palpitations, cœur qui « s’emballe »',
        'Sensation de serrement',
      ],
    },
    {
      icon: Brain,
      title: 'Tête et nerfs',
      items: [
        'Étourdissements, tête légère',
        'Sensation d’irréalité, de « flotter »',
        'Fourmillements autour de la bouche, dans les mains',
        'Vision floue, difficultés de concentration',
        'Mains ou pieds froids',
      ],
    },
    {
      icon: Activity,
      title: 'Muscles et digestion',
      items: [
        'Crampes, contractures, raideurs',
        'Doigts ou mains crispés',
        'Nausées, ventre ballonné',
        'Tension du cou, des épaules, de la mâchoire',
      ],
    },
    {
      icon: Smile,
      title: 'Émotions et énergie',
      items: [
        'Anxiété, peur de manquer d’air',
        'Peur de s’évanouir ou de « faire un malaise »',
        'Fatigue, sommeil perturbé',
        'Hypervigilance : on « écoute » sa respiration',
      ],
    },
  ];

  const causes = [
    {
      title: 'Le stress et l’anxiété',
      desc: 'Le grand facteur déclencheur : stress chronique, anxiété, épisodes de panique. La respiration s’accélère à l’émotion… puis l’habitude s’installe, même au calme.',
    },
    {
      title: 'Les habitudes respiratoires',
      desc: 'Soupirs répétés, bâillements, respiration buccale, « grandes inspirations » compensatrices : des habitudes qui entretiennent la sur-ventilation en boucle.',
    },
    {
      title: 'Posture et écrans',
      desc: 'Posture affaissée (« nuque en avant ») qui comprime le thorax, et « apnée d’écran » : beaucoup de personnes bloquent leur respiration en se concentrant sur un écran.',
    },
    {
      title: 'Après une maladie respiratoire',
      desc: 'Après un épisode d’essoufflement (asthme, infection, covid), on garde l’habitude de « ventiler fort » acquise pendant la maladie — alors que les poumons ont récupéré.',
    },
    {
      title: 'Obstruction nasale',
      desc: 'Nez bouché, rhinite, déviation de cloison : respirer par la bouche augmente le volume ventilé et dérègle le schéma respiratoire.',
    },
    {
      title: 'Déconditionnement physique',
      desc: 'Après une période d’inactivité, l’effort paraît plus dur : on a peur de l’essoufflement, on force sa respiration au lieu de laisser le corps s’adapter.',
    },
  ];

  const exercises = [
    {
      title: 'La prise de conscience',
      duration: '1 à 2 min',
      steps: [
        'Asseyez-vous confortablement, dos droit, épaules relâchées.',
        'Pendant une à deux minutes, observez votre respiration sans la modifier : par le nez ou la bouche ? Thorax ou abdomen ? Soupirs, bâillements, blocages, pauses ?',
        'Repérez aussi les tensions (cou, épaules, mâchoire) et votre état (calme, tendu, anxieux).',
      ],
      closing: 'C’est le point de départ commun à toutes les méthodes : on ne peut changer que ce que l’on perçoit.',
    },
    {
      title: 'La respiration nasale calme',
      duration: '3 à 5 min',
      steps: [
        'Bouche fermée, respirez doucement par le nez.',
        'Réduisez légèrement l’ampleur de chaque respiration : elle doit devenir légère et silencieuse.',
        'Laissez l’expiration s’écouler tranquillement, légèrement plus longue que l’inspiration, sans la forcer.',
        'Cherchez un rythme calme — sans compter obsessionnellement — proche de 10 à 14 cycles par minute.',
      ],
      closing: 'Astuce : une main sur le haut de la poitrine — elle doit rester presque immobile.',
    },
    {
      title: '« Respirez léger » (Breathe Light)',
      duration: '2 à 3 min',
      steps: [
        'Assis ou allongé, respirez normalement par le nez.',
        'Diminuez très légèrement le volume de chaque respiration, comme si vous vouliez la rendre à peine perceptible.',
        'Recherchez une légère envie d’inspirer davantage — la fameuse « faim d’air » légère (air hunger). Cette sensation doit rester légère à modérée et contrôlable.',
        'Maintenez 1 à 2 minutes, puis revenez à une respiration naturelle. Répétez plusieurs fois par jour.',
      ],
      closing: 'Précautions : l’envie de respirer doit rester légère et confortable. Dès qu’elle devient forte, anxiogène ou inconfortable, relâchez immédiatement.',
    },
    {
      title: 'La rétention douce après expiration',
      duration: 'quelques cycles',
      steps: [
        'Après une expiration normale et calme par le nez, pincez doucement le nez.',
        'Gardez la position jusqu’à la première envie nette de respirer (pas plus !).',
        'Relâchez et reprenez une respiration nasale calme, sans grande inspiration de récupération.',
        'Répétez 3 à 5 fois, en espaçant les séries.',
      ],
      closing: 'C’est la base des mesures de la pause de contrôle (Buteyko) et du score BOLT (Oxygen Advantage). Toujours court, doux, et interrompu au premier besoin de respirer.',
    },
    {
      title: 'La marche et l’effort en respiration nasale',
      duration: 'selon condition',
      steps: [
        'Marche : marchez à une allure où vous pouvez respirer uniquement par le nez. Si vous devez ouvrir la bouche, ralentissez.',
        'Marche avec rétention courte (optionnelle) : marchez normalement, expirez doucement, retenez quelques pas, puis reprenez une respiration nasale calme.',
        'Sport : commencez par un jogging ou un effort très léger en respiration nasale ; réduisez l’allure dès que la respiration buccale devient nécessaire.',
        'Récupération : après l’effort, revenez à une respiration nasale calme plutôt que de haleter debout.',
      ],
      closing: 'Ces exercices sont des compléments de l’entraînement — ils ne remplacent ni le travail cardiovasculaire, ni la technique sportive.',
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600;700&display=swap');
        @keyframes heroBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes heroBreatheShadow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.30),
                        0 0 80px rgba(14, 165, 233, 0.10);
          }
          50% {
            box-shadow: 0 0 60px rgba(14, 165, 233, 0.50),
                        0 0 120px rgba(14, 165, 233, 0.20);
          }
        }
        .hero-breathing-orb {
          animation: heroBreathe 4s ease-in-out infinite,
                     heroBreatheShadow 4s ease-in-out infinite;
          will-change: transform, box-shadow;
        }
      `}</style>

      <SiteHeaderV2 />

      {/* ============================ HERO ============================ */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Information patient"
                title="Le syndrome d’"
                highlight="hyperventilation"
                subtitle={<>Comprendre le syndrome d’hyperventilation chronique : symptômes, causes, diagnostic et rééducation par les méthodes <strong>Buteyko®</strong> et <strong>Oxygen Advantage®</strong> pratiquées dans notre cabinet.</>}
                align="left"
                className="mb-6"
              />
              <p
                className="text-xl sm:text-2xl leading-snug mb-8"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.primaryDark }}
              >
                Comprendre et retrouver un souffle calme.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/evaluer-respiration"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  style={{ backgroundColor: C.primary, color: C.white }}
                  data-testid="hero-cta-evaluate"
                >
                  Évaluer votre respiration
                  <ChevronRight size={18} />
                </Link>
                <a
                  href="#nijmegen"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300"
                  style={{
                    backgroundColor: C.white,
                    color: C.primaryDark,
                    border: `1px solid ${C.border}`,
                  }}
                  data-testid="hero-cta-nijmegen"
                >
                  Questionnaire de Nijmegen
                </a>
                <a
                  href="#bolt"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300"
                  style={{
                    backgroundColor: C.white,
                    color: C.primaryDark,
                    border: `1px solid ${C.border}`,
                  }}
                  data-testid="hero-cta-bolt"
                >
                  Calculer mon BOLT
                </a>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="flex items-center justify-center">
                <div
                  className="hero-breathing-orb w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden"
                  style={{ border: `4px solid ${C.skySoft}` }}
                >
                  <img
                    src="/lungs-hyperventilation.jpg"
                    alt="Poumons - Syndrome d'hyperventilation"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================== 1. Qu'est-ce que le SHVC ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="1">Comprendre</SectionLabel>
          <SectionTitle>Qu’est-ce que le syndrome d’hyperventilation chronique ?</SectionTitle>
          <div className="space-y-5 text-lg leading-relaxed" style={{ color: C.slate }}>
            <p>
              Une respiration qui « en fait trop » peut déclencher des symptômes étonnamment
              semblables à ceux d’un manque d’air… alors qu’il s’agit précisément de l’inverse.
            </p>
            <p>
              Le <strong>syndrome d’hyperventilation chronique (SHVC)</strong> désigne une
              respiration habituellement <strong>trop rapide, trop profonde ou trop ample
              par rapport aux besoins réels de l’organisme</strong>. On « ventile » davantage
              que nécessaire — souvent sans même s’en rendre compte — et cette
              sur-ventilation répétée déséquilibre la chimie du sang.
            </p>
            <p>
              La clé pour comprendre ce syndrome tient en un mot : le{' '}
              <strong>dioxyde de carbone (CO₂)</strong>. C’est lui, et non l’oxygène, qui
              est au cœur du problème.
            </p>
          </div>

          <h3
            className="text-2xl mt-12 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Le paradoxe : on manque d’air… parce qu’on respire trop
          </h3>
          <div className="grid sm:grid-cols-2 gap-5 mb-10">
            <div
              className="rounded-2xl p-6"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: C.primaryDark, fontWeight: 500 }}>
                L’oxygène ne manque pas
              </p>
              <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                En respirant abondamment, le sang est parfaitement oxygéné — la saturation
                est souvent normale, voire proche de 100 %. La sensation d’étouffement{' '}
                <strong>ne traduit donc pas un manque d’oxygène</strong> : c’est ce qui la
                rend si déroutante.
              </p>
            </div>
            <div
              className="rounded-2xl p-6"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: C.primaryDark, fontWeight: 500 }}>
                C’est le CO₂ qui s’effondre
              </p>
              <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                Chaque expiration élimine du CO₂. En respirant trop, on en « chasse »
                davantage que la normale : le taux sanguin baisse (<strong>hypocapnie</strong>)
                et le sang devient trop alcalin (<strong>alcalose respiratoire</strong>). Or
                le CO₂ contrôle le tonus des vaisseaux, la sensibilité des nerfs et la
                libération d’oxygène aux tissus. Sa baisse suffit à provoquer une cascade
                de symptômes.
              </p>
            </div>
          </div>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Respiration normale vs hyperventilation
          </h3>
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${C.border}`, backgroundColor: C.white }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: C.skyPale }}>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark, width: '30%' }}>
                    Caractéristique
                  </th>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark }}>
                    Respiration équilibrée
                  </th>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark }}>
                    Hyperventilation
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Fréquence au repos', '10 à 14 cycles par minute', 'Rapide, souvent > 15-20 cycles/min'],
                  ['Ampleur', 'Modérée, silencieuse', 'Grande, ample, parfois bruyante'],
                  ['Voie d’entrée', 'Nez', 'Souvent bouche, soupirs, bâillements'],
                  ['Mouvement', 'Bas des côtes et abdomen', 'Haut de la poitrine, épaules'],
                  ['CO₂ sanguin', 'Équilibré', 'Bas (hypocapnie)'],
                  ['Ressenti', 'Discret, automatique', 'Faim d’air, vigilance, inconfort'],
                ].map(([k, v1, v2], i) => (
                  <tr
                    key={k}
                    style={{
                      backgroundColor: i % 2 === 0 ? C.cloud : C.white,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <td className="py-3 px-5 font-medium" style={{ color: C.ink }}>{k}</td>
                    <td className="py-3 px-5" style={{ color: C.slate }}>{v1}</td>
                    <td className="py-3 px-5" style={{ color: C.slate }}>{v2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <Callout icon={ClipboardList} title="À retenir">
              Le SHVC est un <strong>trouble fonctionnel</strong> — la respiration est mal
              réglée, sans lésion des poumons ni du cœur. Il peut être persistant,
              fluctuant, ou survenir par épisodes (souvent en situation de stress). Il n’est
              « pas dans la tête » pour autant : l’hyperventilation modifie réellement la
              chimie du sang et déclenche de vrais symptômes physiques.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 2. Symptômes ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="2">Reconnaître</SectionLabel>
          <SectionTitle>Les symptômes : un tableau très varié</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Les symptômes du SHVC touchent de nombreux systèmes et peuvent imiter une
            maladie respiratoire, cardiaque ou neurologique. C’est pourquoi ils nécessitent
            toujours une vérification médicale.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {symptomGroups.map((g) => (
              <SymptomGroup key={g.title} {...g} />
            ))}
          </div>

          <div className="mt-10 space-y-4">
            <Callout variant="info" icon={ClipboardList} title="À retenir">
              Ces symptômes <strong>peuvent ressembler à ceux d’une maladie grave</strong>
              {' '}(cœur, poumons, neurologie). Une évaluation médicale est indispensable
              pour écarter toute autre cause avant d’orienter vers une rééducation respiratoire.
            </Callout>
            <Callout variant="danger" icon={AlertTriangle} title="⚠ Symptômes d’alerte — consultez sans attendre">
              Douleur thoracique brutale ou intense, essoufflement soudain et sévère,
              malaise ou évanouissement, lèvres ou visage bleutés, difficulté à parler,
              crachats sanglants. Appelez immédiatement les <strong>urgences (144)</strong>.
              Voir la section « Quand consulter » en fin de page.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 3. Causes ================== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="3">Origines</SectionLabel>
          <SectionTitle>Les causes et facteurs favorisants</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Le mécanisme est le plus souvent multifactoriel : plusieurs éléments se combinent
            pour « dérégler » le réglage de la respiration.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {causes.map((c, i) => (
              <div
                key={c.title}
                className="rounded-2xl p-6 h-full transition-all duration-300 hover:-translate-y-1"
                style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-3 text-sm font-semibold"
                  style={{ backgroundColor: C.skyPale, color: C.primaryDark }}
                >
                  {i + 1}
                </div>
                <h3 className="text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}>
                  {c.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Callout variant="warning" icon={AlertTriangle} title="Attention">
              Certaines <strong>maladies</strong> peuvent aussi stimuler la respiration et
              provoquer une hyperventilation secondaire : maladie pulmonaire, problème
              cardiaque, anémie, infection, acidose métabolique, embolie pulmonaire… C’est
              précisément pour cela que le diagnostic médical est la première étape
              indispensable : on ne doit pas attribuer tout essoufflement à l’anxiété ou à
              l’hyperventilation.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 4. Cercle vicieux ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="4">Mécanisme</SectionLabel>
          <SectionTitle>Le cercle vicieux de l’hyperventilation</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed" style={{ color: C.slate }}>
            Comprendre le mécanisme qui s’auto-entretient, c’est déjà commencer à le briser.
          </p>
          <ol className="space-y-5">
            {[
              ['Un déclencheur', 'Stress, émotion, habitude, effort, congestion nasale — provoque une respiration excessive ou irrégulière.'],
              ['Le CO₂ sanguin baisse', 'On élimine trop de dioxyde de carbone : hypocapnie et alcalose respiratoire s’installent.'],
              ['Les symptômes apparaissent', 'Vertiges, fourmillements, oppression, palpitations, faim d’air… Ce sont les effets directs du déséquilibre du CO₂, pas d’un manque d’oxygène.'],
              ['Ces sensations font peur', 'On se sent menacé, on respire encore plus fort, on « écoute » sa respiration, on teste son souffle…'],
              ['L’hyperventilation s’aggrave', 'Ce qui accentue les symptômes et l’anxiété. Le cercle est bouclé.'],
            ].map(([title, desc], i) => (
              <li
                key={i}
                className="flex gap-5 rounded-2xl p-5"
                style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: C.primary,
                    color: C.white,
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }}
                >
                  {i + 1}
                </div>
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: C.ink }}>{title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>{desc}</p>
                </div>
              </li>
            ))}
          </ol>

          <div
            className="mt-10 rounded-2xl p-8 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              color: C.white,
            }}
          >
            <p className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              « Respirez léger pour respirer juste. »
            </p>
            <p className="text-sm opacity-90">
              Principe commun aux méthodes Buteyko® et Oxygen Advantage® : moins de
              ventilation excessive, plus de confort respiratoire.
            </p>
          </div>

          <p className="text-lg leading-relaxed mt-10" style={{ color: C.slate }}>
            La bonne nouvelle : ce cercle <strong>peut être inversé</strong>. Les méthodes
            Buteyko® et Oxygen Advantage® apprennent à reconnaître la respiration excessive,
            à la calmer progressivement et à <strong>réhabituer l’organisme</strong> à un
            niveau de CO₂ équilibré. Les symptômes s’estompent alors, la peur diminue, et
            la respiration redevient un geste discret et fiable.
          </p>
        </div>
      </section>

      {/* ================== 5. Diagnostic ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="5">Diagnostic</SectionLabel>
          <SectionTitle>Comment le diagnostic est-il posé ?</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed" style={{ color: C.slate }}>
            Le diagnostic du syndrome d’hyperventilation est essentiellement{' '}
            <strong>clinique et d’exclusion</strong> : le médecin vérifie qu’aucune autre
            maladie n’explique les symptômes, puis le praticien complète par un bilan
            fonctionnel de la respiration.
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div
              className="rounded-2xl p-6"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
              >
                <Stethoscope size={20} />
              </div>
              <h4 className="font-semibold mb-2" style={{ color: C.ink }}>
                Étape médicale (indispensable)
              </h4>
              <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                Interrogatoire, examen clinique, saturation en oxygène, et selon le
                contexte électrocardiogramme, radiographie du thorax, spirométrie, bilan
                sanguin, gaz du sang…
              </p>
            </div>
            <div
              className="rounded-2xl p-6"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
              >
                <Eye size={20} />
              </div>
              <h4 className="font-semibold mb-2" style={{ color: C.ink }}>
                Étape en rééducation
              </h4>
              <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                Questionnaire de Nijmegen (ci-dessous), observation du schéma respiratoire
                au repos et au mouvement, évaluation posturale, et éventuellement mesure du
                CO₂ expiré (capnographie) lors d’un test de provocation encadré.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================== 6. Nijmegen (interactif) ================== */}
      <section id="nijmegen" className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>Auto-évaluation</SectionLabel>
          <SectionTitle>Le questionnaire de Nijmegen</SectionTitle>
          <p className="text-lg mb-8 leading-relaxed" style={{ color: C.slate }}>
            Le <strong>questionnaire de Nijmegen</strong> est l’outil de dépistage le plus
            utilisé pour orienter vers une hyperventilation ou un trouble du schéma
            respiratoire. Il comporte 16 symptômes, cotés de 0 (jamais) à 4 (très souvent).
            Vous pouvez le remplir ci-dessous avant votre rendez-vous.
          </p>

          <div
            className="rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{ backgroundColor: C.skyPale, border: `1px solid ${C.skySoft}` }}
          >
            <div>
              <p className="text-xs uppercase tracking-[0.2em] mb-1" style={{ color: C.primaryDark, fontWeight: 500 }}>
                Votre score total
              </p>
              <p
                className="text-4xl sm:text-5xl"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
                data-testid="nijmegen-total-score"
              >
                {totalScore} <span className="text-2xl" style={{ color: C.muted }}>/ 64</span>
              </p>
              <p className="text-xs mt-1" style={{ color: C.slate }}>
                {answeredCount} / {NIJMEGEN_ITEMS.length} questions répondues
              </p>
            </div>
            <button
              type="button"
              onClick={resetAnswers}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: C.white, color: C.primaryDark, border: `1px solid ${C.border}` }}
              data-testid="nijmegen-reset"
            >
              <RotateCcw size={14} />
              Effacer mes réponses
            </button>
          </div>

          <div
            className="rounded-xl p-4 mb-8 text-sm"
            style={{
              backgroundColor: C.white,
              border: `1px solid ${C.border}`,
              color: scoreState.color,
              fontWeight: 500,
            }}
            data-testid="nijmegen-interpretation"
          >
            {scoreState.label}
          </div>

          <div className="space-y-3">
            {NIJMEGEN_ITEMS.map((label, i) => (
              <div
                key={label}
                className="rounded-xl p-4 sm:p-5"
                style={{
                  backgroundColor: C.white,
                  border: `1px solid ${answers[i] !== null ? C.skySoft : C.border}`,
                }}
              >
                <p className="text-sm font-medium mb-3" style={{ color: C.ink }}>
                  {i + 1}. {label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {NIJMEGEN_LEVELS.map((lvl) => {
                    const active = answers[i] === lvl.value;
                    return (
                      <button
                        key={lvl.value}
                        type="button"
                        onClick={() => setAnswer(i, lvl.value)}
                        className="text-xs px-3 py-2 rounded-full transition-all"
                        style={{
                          backgroundColor: active ? C.primary : C.cloud,
                          color: active ? C.white : C.slate,
                          border: `1px solid ${active ? C.primary : C.border}`,
                          fontWeight: active ? 600 : 400,
                        }}
                        data-testid={`nijmegen-q${i}-l${lvl.value}`}
                      >
                        {lvl.value} · {lvl.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Callout icon={ClipboardList} title="Lecture des résultats">
              Un score supérieur à <strong>23 / 64</strong> suggère un syndrome
              d’hyperventilation. Un score inférieur à 23 mais avec{' '}
              <strong>au moins 3 symptômes cotés « souvent » ou « très souvent »</strong>
              {' '}peut orienter vers un trouble du schéma respiratoire.{' '}
              <strong>Ce questionnaire est un outil d’orientation, pas un diagnostic</strong>
              {' '}: son interprétation se fait toujours avec votre praticien, après avis médical.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 7. Deux méthodes ================== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="6">Notre approche</SectionLabel>
          <SectionTitle>Deux méthodes complémentaires</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Dans notre cabinet, nous utilisons deux approches de rééducation respiratoire
            reconnues. Elles partagent les mêmes fondations physiologiques — respiration
            nasale, douceur, réduction de la ventilation excessive — mais se distinguent
            par leurs objectifs : la santé et le contrôle respiratoire pour la méthode
            Buteyko®, la performance et le sport pour l’Oxygen Advantage®.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div
              className="rounded-2xl p-6 sm:p-8"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
              >
                <Stethoscope size={22} />
              </div>
              <h3
                className="text-2xl mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
              >
                Méthode Buteyko®
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: C.slate }}>
                Développée par le médecin <strong>Konstantin Buteyko</strong> (années 1950),
                centrée sur la santé : réduire la sur-ventilation chronique, notamment chez
                les personnes asthmatiques ou sujettes à l’hyperventilation et à l’essoufflement.
              </p>
              <div
                className="rounded-lg p-3 text-xs"
                style={{ backgroundColor: C.skyPale, color: C.primaryDark }}
              >
                <strong>Outil de suivi :</strong> la <em>pause de contrôle</em> (Control Pause),
                mesure de la première envie de respirer.
              </div>
            </div>

            <div
              className="rounded-2xl p-6 sm:p-8"
              style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
              >
                <Zap size={22} />
              </div>
              <h3
                className="text-2xl mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
              >
                Oxygen Advantage®
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: C.slate }}>
                Créée par <strong>Patrick McKeown</strong>, elle modernise ces principes et
                les oriente vers la <strong>performance sportive</strong>, la tolérance à
                l’effort et l’essoufflement chez les sportifs comme chez les patients.
              </p>
              <div
                className="rounded-lg p-3 text-xs"
                style={{ backgroundColor: C.skyPale, color: C.primaryDark }}
              >
                <strong>Outil de suivi :</strong> le <em>score BOLT</em> (Body Oxygen Level
                Test), indicateur de tolérance au CO₂ et d’efficacité ventilatoire.
              </div>
            </div>
          </div>

          {/* Comparison table */}
          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Tableau comparatif
          </h3>
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${C.border}`, backgroundColor: C.white }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: C.skyPale }}>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark, width: '24%' }}></th>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark }}>
                    Méthode Buteyko®
                  </th>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark }}>
                    Oxygen Advantage®
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Origine', 'Konstantin Buteyko, médecin, années 1950 (URSS)', 'Patrick McKeown, années 2000-2010 (Irlande)'],
                  ['Objectif premier', 'Santé : asthme, hyperventilation, essoufflement', 'Performance : sport, effort, respiration nasale à l’exercice'],
                  ['Mesure de suivi', 'Pause de contrôle (CP)', 'Score BOLT'],
                  ['Public type', 'Patients, asthmatiques, personnes anxieuses', 'Sportifs, coureurs, personnes actives'],
                ].map(([k, v1, v2], i) => (
                  <tr
                    key={k}
                    style={{
                      backgroundColor: i % 2 === 0 ? C.cloud : C.white,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <td className="py-3 px-5 font-medium" style={{ color: C.ink }}>{k}</td>
                    <td className="py-3 px-5" style={{ color: C.slate }}>{v1}</td>
                    <td className="py-3 px-5" style={{ color: C.slate }}>{v2}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `1px solid ${C.border}`, backgroundColor: C.cloud }}>
                  <td className="py-3 px-5 font-medium" style={{ color: C.ink }}>Principes de base</td>
                  <td className="py-3 px-5" colSpan={2} style={{ color: C.slate }}>
                    Respirer par le nez, moins amplement, plus lentement, avec le
                    diaphragme ; tolérer une légère envie de respirer.
                  </td>
                </tr>
                <tr style={{ borderTop: `1px solid ${C.border}`, backgroundColor: C.white }}>
                  <td className="py-3 px-5 font-medium" style={{ color: C.ink }}>Cadre</td>
                  <td className="py-3 px-5" colSpan={2} style={{ color: C.slate }}>
                    Toujours un apprentissage encadré par un professionnel formé, en
                    complément du suivi médical.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <Callout icon={Search} title="Lequel choisir ?">
              Ce n’est pas une question de « meilleure » méthode, mais d’<strong>objectifs
              et de profil</strong>. Votre praticien vous orientera lors du bilan : la
              méthode Buteyko® est souvent privilégiée pour les troubles de santé liés à
              l’hyperventilation, l’Oxygen Advantage® pour la dimension sportive et
              l’effort. Beaucoup de patients bénéficient des deux, dont les principes se
              renforcent mutuellement.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 7. Méthode Buteyko ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="7">Focus</SectionLabel>
          <SectionTitle>La méthode Buteyko®</SectionTitle>
          <p className="text-lg leading-relaxed mb-10" style={{ color: C.slate }}>
            Une méthode médicale historique pour réapprendre à respirer « juste assez ».
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Son histoire
          </h3>
          <p className="text-base leading-relaxed mb-8" style={{ color: C.slate }}>
            La méthode porte le nom de <strong>Konstantin Pavlovich Buteyko</strong>, médecin
            soviétique qui a consacré sa carrière à l’étude de la respiration excessive
            (<em>deep breathing</em>). Il a observé que de nombreuses personnes respiraient
            « trop » par rapport aux besoins de leur organisme, et que cette sur-ventilation
            chronique pouvait entretenir l’asthme, l’essoufflement et divers symptômes. Sa
            méthode, développée à partir des années 1950, vise à{' '}
            <strong>rééduquer progressivement la respiration</strong> pour qu’elle
            redevienne calme, silencieuse et adaptée.
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Ses principes
          </h3>
          <div
            className="rounded-2xl p-6 mb-8"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <ul className="space-y-3">
              {[
                ['Respirer par le nez', 'autant que possible — au repos, pendant le sommeil, et lors d’un effort léger.'],
                ['Moins ample et plus silencieuse', 'on ne cherche jamais à « remplir » ses poumons au repos.'],
                ['Diaphragmatique', 'mouvement doux de l’abdomen plutôt qu’un soulèvement du haut du thorax.'],
                ['Ralentir progressivement', 'la fréquence respiratoire.'],
                ['Courtes pauses respiratoires contrôlées', 'pour réduire la tendance à l’hyperventilation.'],
                ['Tolérer une légère envie de respirer', 'sans jamais aller jusqu’à la détresse ni à l’apnée forcée.'],
              ].map(([k, v]) => (
                <li key={k} className="flex gap-3 text-sm" style={{ color: C.slate }}>
                  <span
                    className="mt-1.5 rounded-full flex-shrink-0"
                    style={{ width: 6, height: 6, backgroundColor: C.primary }}
                  />
                  <span>
                    <strong style={{ color: C.ink }}>{k}</strong> — {v}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-base leading-relaxed mb-10" style={{ color: C.slate }}>
            L’idée centrale : <strong>réhabituer l’organisme à un niveau de CO₂ équilibré</strong>,
            en réduisant la ventilation excessive de façon douce et progressive.
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            La pause de contrôle (Control Pause)
          </h3>
          <p className="text-base leading-relaxed mb-6" style={{ color: C.slate }}>
            La <strong>pause de contrôle (CP)</strong> est l’indicateur de suivi de la
            méthode. Il est important de comprendre ce qu’elle mesure :{' '}
            <strong>pas</strong> la durée maximale pendant laquelle on peut retenir sa
            respiration, mais le temps qui s’écoule avant la{' '}
            <strong>première envie nette de reprendre l’air</strong>.
          </p>
          <div
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <ol className="space-y-3">
              {[
                'Asseyez-vous droit, détendu, et respirez normalement par le nez pendant quelques minutes.',
                'Faites une inspiration normale, puis une expiration normale et calme.',
                'Après cette expiration, pincez doucement le nez et lancez le chronomètre.',
                'Arrêtez dès la première envie nette de respirer ou au premier inconfort respiratoire évident.',
                'Relâchez le nez et reprenez une respiration normale, sans inspirer brutalement.',
              ].map((step, idx) => (
                <li key={idx} className="flex gap-3 text-sm" style={{ color: C.slate }}>
                  <span
                    className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: C.skyPale,
                      color: C.primaryDark,
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <Callout variant="warning" icon={AlertTriangle} title="Règles de sécurité">
            Ne forcez jamais, ne « faites pas un record », n’allez jamais jusqu’aux
            contractions, aux vertiges ou à la détresse. Si la respiration devient plus
            forte ou plus rapide après la mesure, c’est que la pause a été trop longue. La
            CP n’est pas un test médical de diagnostic : c’est un indicateur personnel de
            suivi, influencé par le sommeil, le stress, la congestion nasale ou la technique.
          </Callout>

          <p className="text-base leading-relaxed mt-8" style={{ color: C.slate }}>
            En pratique, votre praticien mesure la CP en séance, vous apprend à la mesurer
            correctement, et vous la faites évoluer au fil des semaines : une CP qui
            s’allonge progressivement traduit une respiration qui se calme.
          </p>
        </div>
      </section>

      {/* ================== 8. Oxygen Advantage ================== */}
      <section id="bolt" className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="8">Focus</SectionLabel>
          <SectionTitle>L’Oxygen Advantage®</SectionTitle>
          <p className="text-lg leading-relaxed mb-10" style={{ color: C.slate }}>
            Une approche moderne de la respiration fonctionnelle, tournée vers l’effort et
            la performance.
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Son histoire
          </h3>
          <p className="text-base leading-relaxed mb-8" style={{ color: C.slate }}>
            L’<strong>Oxygen Advantage®</strong> a été créé par{' '}
            <strong>Patrick McKeown</strong>, auteur et spécialiste irlandais de la
            respiration fonctionnelle. Formé à la méthode Buteyko, il en a repris les
            fondements — respiration nasale, douceur, réduction de la ventilation — pour
            les <strong>moderniser et les orienter vers la performance sportive</strong>,
            la gestion de l’essoufflement à l’effort et la santé en général. Le programme
            est organisé autour de trois axes : <strong>Body</strong> (le corps),{' '}
            <strong>Mind</strong> (l’esprit) et <strong>Sport</strong> (la performance).
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Ses principes
          </h3>
          <div
            className="rounded-2xl p-6 mb-8"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <ul className="space-y-3">
              {[
                ['Respirer par le nez', 'au repos, pendant le sommeil et autant que possible à l’effort. Le nez filtre, réchauffe, humidifie l’air et produit du monoxyde d’azote, bénéfique pour les voies respiratoires et les vaisseaux.'],
                ['Respirer moins, plus lentement et plus silencieusement', 'l’objectif n’est pas de prendre de grandes inspirations, mais de réduire progressivement le volume respiratoire excessif.'],
                ['Respirer avec le diaphragme', 'en limitant les mouvements du haut du thorax, des épaules et du cou.'],
                ['Développer la tolérance au CO₂ et à l’essoufflement', 'moins de ventilation excessive, c’est moins de perte de CO₂ et donc un essoufflement qui survient plus tard à l’effort.'],
                ['Associer respiration et mouvement', 'marche, course légère, et courtes rétentions respiratoires destinées à s’habituer progressivement à la sensation d’envie de respirer.'],
              ].map(([k, v]) => (
                <li key={k} className="flex gap-3 text-sm" style={{ color: C.slate }}>
                  <span
                    className="mt-1.5 rounded-full flex-shrink-0"
                    style={{ width: 6, height: 6, backgroundColor: C.primary }}
                  />
                  <span>
                    <strong style={{ color: C.ink }}>{k}</strong> — {v}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* BOLT explanation */}
          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Le score BOLT
          </h3>
          <p className="text-base leading-relaxed mb-6" style={{ color: C.slate }}>
            Le <strong>BOLT</strong> (<em>Body Oxygen Level Test</em>) est l’indicateur de
            suivi de l’Oxygen Advantage®. Malgré son nom, il ne mesure <strong>pas</strong>
            {' '}le taux d’oxygène dans le sang : c’est un test comportemental de{' '}
            <strong>tolérance au CO₂ et de sensibilité à l’envie de respirer</strong>,
            indirectement lié au profil ventilatoire au repos.
          </p>
          <div
            className="rounded-2xl p-6 mb-8"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <ol className="space-y-3">
              {[
                'Reposez-vous calmement pendant au moins 10 minutes, idéalement le matin.',
                'Asseyez-vous dos droit, épaules relâchées.',
                'Prenez une inspiration normale et douce par le nez, puis expirez normalement — sans vider les poumons de force.',
                'Pincez le nez et démarrez le chronomètre.',
                'Arrêtez au premier besoin net de respirer (envie d’avaler, contraction de la gorge ou de l’abdomen, tension des voies respiratoires) — pas quand vous êtes obligé de reprendre votre souffle.',
                'Relâchez le nez et reprenez une inspiration calme par le nez, sans haleter.',
              ].map((step, idx) => (
                <li key={idx} className="flex gap-3 text-sm" style={{ color: C.slate }}>
                  <span
                    className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: C.skyPale,
                      color: C.primaryDark,
                    }}
                  >
                    {idx + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <p className="text-base italic mb-8" style={{ color: C.primaryDark }}>
            Le BOLT ne mesure donc pas la durée maximale de rétention : il mesure le temps
            jusqu’à la première réaction respiratoire claire.
          </p>

          {/* BOLT interpretation table */}
          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Interprétation indicative du score BOLT
          </h3>
          <div
            className="rounded-2xl overflow-hidden mb-8"
            style={{ border: `1px solid ${C.border}`, backgroundColor: C.white }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: C.skyPale }}>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark, width: '25%' }}>
                    Score BOLT
                  </th>
                  <th className="py-3 px-5 text-left font-medium" style={{ color: C.primaryDark }}>
                    Interprétation pratique (Oxygen Advantage®)
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Moins de 10 s', 'Respiration probablement bruyante, irrégulière ou excessive ; marge d’amélioration importante'],
                  ['10 – 20 s', 'Fondations respiratoires à développer ; congestion nasale, mauvais sommeil ou respiration buccale possibles'],
                  ['20 – 30 s', 'Efficacité respiratoire correcte, avec potentiel d’amélioration'],
                  ['30 – 40 s', 'Très bonne tolérance au CO₂ et respiration efficace'],
                  ['40 s et plus', 'Niveau de référence présenté par la méthode'],
                ].map(([k, v], i) => (
                  <tr
                    key={k}
                    style={{
                      backgroundColor: i % 2 === 0 ? C.cloud : C.white,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <td className="py-3 px-5 font-medium" style={{ color: C.ink }}>{k}</td>
                    <td className="py-3 px-5" style={{ color: C.slate }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-sm leading-relaxed mb-10" style={{ color: C.slate }}>
            Ces seuils sont ceux proposés par la méthode et ne constituent pas des normes
            médicales universelles. Le score varie selon le sommeil, le stress, la
            congestion nasale, la caféine ou la technique.{' '}
            <strong>Ce qui compte, c’est la tendance sur plusieurs semaines</strong>, pas
            une mesure isolée.
          </p>

          {/* BOLT Calculator */}
          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Calculez votre score BOLT
          </h3>
          <div
            className="rounded-2xl p-6"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
              >
                <Gauge size={20} />
              </div>
              <h4 className="font-semibold" style={{ color: C.ink }}>
                Mon score BOLT
              </h4>
            </div>
            <label className="text-sm block mb-3" style={{ color: C.slate }}>
              Entrez le temps (en secondes) mesuré jusqu’à votre première envie nette de respirer :
            </label>
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="number"
                min="0"
                max="120"
                step="1"
                value={boltInput}
                onChange={(e) => setBoltInput(e.target.value)}
                placeholder="ex : 22"
                className="rounded-xl px-4 py-3 text-base outline-none transition-all"
                style={{
                  border: `1px solid ${C.border}`,
                  color: C.ink,
                  backgroundColor: C.cloud,
                  width: 180,
                }}
                data-testid="bolt-input"
              />
              <span className="text-sm" style={{ color: C.muted }}>secondes</span>
              <button
                type="button"
                onClick={() => setBoltInput('')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: C.cloud,
                  color: C.primaryDark,
                  border: `1px solid ${C.border}`,
                }}
                data-testid="bolt-reset"
              >
                <RotateCcw size={14} />
                Effacer
              </button>
            </div>
            <div
              className="rounded-xl p-4 mt-5 text-sm"
              style={{
                backgroundColor: C.cloud,
                border: `1px solid ${C.border}`,
                color: boltState.color,
                fontWeight: 500,
              }}
              data-testid="bolt-interpretation"
            >
              {boltState.label}
            </div>
          </div>

          <div className="mt-6">
            <Callout variant="warning" icon={AlertTriangle}>
              Ce calculateur est un outil éducatif reprenant l’échelle proposée par Oxygen
              Advantage®. Il ne remplace pas l’évaluation de votre praticien ni l’avis
              médical. Ne pratiquez jamais de rétention en cas de malaise, de grossesse à
              risque ou de pathologie cardiovasculaire non stabilisée, et arrêtez au
              premier inconfort.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 9. Exercices ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="9">Pratique</SectionLabel>
          <SectionTitle>Les exercices de rééducation</SectionTitle>
          <p className="text-lg mb-8 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Les exercices ci-dessous sont présentés à titre éducatif. Ils sont{' '}
            <strong>enseignés et encadrés par votre praticien</strong>, qui les adaptera à
            votre cas, à vos objectifs et à votre état de santé.
          </p>

          <div className="mb-10">
            <Callout variant="warning" icon={AlertTriangle} title="⚠ Règles de sécurité absolues">
              Arrêtez immédiatement en cas de vertiges importants, douleur thoracique,
              malaise ou aggravation de l’essoufflement.{' '}
              <strong>Ne pratiquez jamais d’apnées prolongées</strong> : les pauses
              respiratoires utilisées dans ces méthodes sont courtes, douces et toujours
              interrompues au premier besoin de respirer. En cas d’asthme, de BPCO, de
              grossesse ou de maladie cardiovasculaire, ces exercices se pratiquent
              uniquement avec l’accord et l’encadrement de votre praticien.
            </Callout>
          </div>

          <div
            className="rounded-2xl p-6 sm:p-8 mb-10 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.skyPale} 0%, ${C.skySoft} 100%)`,
              border: `1px solid ${C.skySoft}`,
            }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div>
                <p className="text-sm font-medium" style={{ color: C.primaryDark }}>
                  ↑ Inspirez par le nez (≈ 3-4 s)
                </p>
                <p className="text-sm font-medium mt-1" style={{ color: C.primaryDark }}>
                  ↓ Expirez doucement (≈ 4-5 s)
                </p>
              </div>
            </div>
            <p className="text-sm mt-4 max-w-xl mx-auto" style={{ color: C.slate }}>
              Suivez le mouvement du cercle. L’expiration est légèrement plus longue que
              l’inspiration, sans forcer. La respiration reste légère et silencieuse — on
              ne « remplit » pas ses poumons.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {exercises.map((e, i) => (
              <ExerciseCard key={e.title} index={i + 1} {...e} />
            ))}
          </div>

          <div className="mt-14">
            <h3
              className="text-2xl mb-6"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
            >
              Conseils pour une pratique efficace
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  title: 'Régularité',
                  desc: 'De courtes sessions (5 à 10 minutes) 2 à 3 fois par jour valent mieux qu’une longue session hebdomadaire.',
                },
                {
                  title: 'Douceur',
                  desc: 'Tout doit rester confortable. Une « faim d’air » légère est normale ; la détresse, la lutte ou les vertiges sont des signaux d’arrêt.',
                },
                {
                  title: 'Ancrage',
                  desc: 'Associez la pratique à des moments fixes : au réveil, aux pauses, en marchant, avant de dormir. Et surtout : au nez, même la nuit.',
                },
                {
                  title: 'Patience',
                  desc: 'Les progrès ne sont pas linéaires : le stress ou la fatigue font resurgir l’ancien schéma. C’est le signal de ressortir vos outils, pas un échec.',
                },
              ].map((tip) => (
                <div
                  key={tip.title}
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
                >
                  <p
                    className="text-xs uppercase tracking-[0.2em] mb-2"
                    style={{ color: C.primaryDark, fontWeight: 500 }}
                  >
                    {tip.title}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================== 10. Déroulement ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="10">Prise en charge</SectionLabel>
          <SectionTitle>Comment se déroule la prise en charge ?</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed" style={{ color: C.slate }}>
            Chaque parcours est unique, mais voici à quoi ressemble en général une
            rééducation par les méthodes Buteyko® et/ou Oxygen Advantage® dans notre cabinet.
          </p>
          <div className="space-y-4">
            {[
              {
                icon: Stethoscope,
                title: '1. La séance de bilan (45 à 60 min)',
                desc: 'Entretien approfondi, questionnaire de Nijmegen, observation de la respiration, mesures de référence (pause de contrôle ou score BOLT selon votre profil). Vous repartez avec une compréhension claire de votre respiration et vos premiers outils.',
              },
              {
                icon: Activity,
                title: '2. Les séances de rééducation (environ 30 min)',
                desc: 'Apprentissage et correction des exercices, travail postural et de relâchement, intégration dans vos activités (marche, escaliers, sport). La fréquence est adaptée à votre situation et à vos objectifs.',
              },
              {
                icon: Wind,
                title: '3. La pratique à la maison',
                desc: '5 à 10 minutes par jour, 2 à 3 fois par jour : respiration nasale calme, exercices de légèreté, rétentions douces, marche nasale. C’est la pratique régulière qui transforme la respiration.',
              },
              {
                icon: Gauge,
                title: '4. Le suivi des mesures',
                desc: 'Au fil des semaines, la pause de contrôle ou le score BOLT s’allonge progressivement : c’est un marqueur objectif de progrès. Le questionnaire de Nijmegen est refait régulièrement pour confirmer la diminution des symptômes.',
              },
              {
                icon: Smile,
                title: '5. L’autonomie',
                desc: 'L’objectif final : une respiration calme, nasale et silencieuse devenue automatique, et une boîte à outils pour les moments de stress, d’effort ou les rechutes occasionnelles.',
              },
            ].map((s) => (
              <div
                key={s.title}
                className="rounded-2xl p-5 flex gap-4"
                style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
                >
                  <s.icon size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: C.ink }}>
                    {s.title}
                  </h4>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Callout icon={ClipboardList} title="Résultats attendus">
              Beaucoup de patients constatent un mieux-être en quelques semaines de
              pratique régulière (calme, moins de soupirs, moins d’essoufflement, meilleur
              sommeil). Le parcours complet se consolide sur plusieurs mois. Chacun évolue
              à son rythme — l’important est la régularité.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 11. FAQ ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="11">FAQ</SectionLabel>
          <SectionTitle>Questions fréquentes</SectionTitle>
          <div className="space-y-3 mt-8">
            <FaqItem index={0} q="Si je respire trop, pourquoi ai-je l’impression de manquer d’air ?">
              <p>
                C’est le paradoxe central de l’hyperventilation : en respirant plus que
                nécessaire, vous éliminez trop de CO₂. Or ce gaz, à son niveau normal,
                contribue à la sensation de confort respiratoire. Quand il baisse, les
                récepteurs du cerveau interprètent mal la situation et déclenchent une
                sensation de faim d’air — <strong>alors même que le sang est parfaitement
                oxygéné</strong>. C’est pourquoi « respirer encore plus fort » aggrave le
                problème, tandis que calmer la respiration le résout.
              </p>
            </FaqItem>
            <FaqItem index={1} q="Est-ce que le syndrome d’hyperventilation est « psychologique » ?">
              <p>
                Le stress et l’anxiété sont des déclencheurs majeurs, et le trouble est
                souvent lié à la sphère émotionnelle. Mais il n’est pas « imaginaire » :
                l’hyperventilation <strong>modifie réellement la chimie du sang</strong>
                {' '}(baisse du CO₂, alcalose) et provoque de vrais symptômes physiques.
                C’est un trouble à la fois physiologique et psychologique — d’où
                l’efficacité d’une méthode qui travaille le corps (respiration, posture)
                et l’esprit (relaxation, gestion du stress) ensemble.
              </p>
            </FaqItem>
            <FaqItem index={2} q="Buteyko ou Oxygen Advantage : quelle méthode est faite pour moi ?">
              <p>
                Tout dépend de vos objectifs. La <strong>méthode Buteyko®</strong> est
                historiquement centrée sur la santé : asthme, hyperventilation,
                essoufflement au quotidien. L’<strong>Oxygen Advantage®</strong> reprend
                les mêmes fondations en les orientant vers la <strong>performance et
                l’effort</strong> (sport, course, tolérance à l’essoufflement). Les deux
                se complètent ; votre praticien vous orientera lors du bilan, et beaucoup
                de patients bénéficient des principes des deux méthodes.
              </p>
            </FaqItem>
            <FaqItem index={3} q="Est-il dangereux de retenir sa respiration ?">
              <p>
                Les rétentions utilisées dans ces méthodes ne sont <strong>pas</strong> des
                apnées : ce sont des pauses <strong>courtes et douces</strong>,
                interrompues dès la première envie nette de respirer. Elles ne doivent
                jamais être forcées ni prolongées. Pratiquées avec un professionnel et
                dans le respect des consignes, elles sont généralement sans danger. En
                revanche, <strong>jamais</strong> d’apnées longues ou forcées sans
                encadrement, et jamais de rétention en cas de pathologie cardiovasculaire
                non stabilisée, de grossesse à risque ou de malaise.
              </p>
            </FaqItem>
            <FaqItem index={4} q="Ces méthodes remplacent-elles mon traitement (asthme, anxiété…) ?">
              <p>
                <strong>Absolument pas.</strong> La rééducation respiratoire est{' '}
                <strong>complémentaire</strong> du traitement médical, jamais substitutive.
                Pour l’asthme notamment, la méthode Buteyko® a montré des bénéfices dans
                des études, mais elle ne remplace ni les bronchodilatateurs ni la
                corticothérapie : elle peut aider à mieux contrôler les symptômes et à
                réduire le recours aux médicaments <strong>sous supervision médicale</strong>.
                Ne modifiez jamais un traitement sans l’accord de votre médecin.
              </p>
            </FaqItem>
            <FaqItem index={5} q="Quelle est la différence entre la pause de contrôle et le score BOLT ?">
              <p>
                Ce sont deux mesures très proches, issues de deux méthodes sœurs : toutes
                deux mesurent le temps jusqu’à la <strong>première envie nette de
                respirer</strong> après une expiration normale (et non la durée maximale
                de rétention). La <strong>pause de contrôle (CP)</strong> est utilisée par
                la méthode Buteyko®, le <strong>score BOLT</strong> par l’Oxygen
                Advantage®. Les consignes de mesure sont quasi identiques, avec de légères
                différences de protocole. L’essentiel : les deux servent à{' '}
                <strong>suivre votre progression</strong> dans le temps, pas à poser un
                diagnostic.
              </p>
            </FaqItem>
            <FaqItem index={6} q="Pourquoi mes symptômes reviennent-ils parfois, même quand ça va mieux ?">
              <p>
                C’est normal : l’ancien schéma respiratoire resurgit volontiers lors de
                stress, de fatigue ou de surcharge. Ce n’est pas un échec — c’est le
                signal qu’il faut ressortir vos outils (respiration nasale calme,
                exercices de légèreté, pause douce). Avec le temps, les rechutes deviennent
                plus courtes, plus légères et plus rares, jusqu’à devenir de simples
                « pensées » sans impact.
              </p>
            </FaqItem>
            <FaqItem index={7} q="Puis-je pratiquer seule à la maison ?">
              <p>
                Oui — c’est même essentiel à la réussite — <strong>après un apprentissage
                guidé avec votre praticien</strong>. Les exercices « vus sur Internet »
                sans encadrement comportent des risques (apnées trop longues, techniques
                erronées, absence d’adaptation à votre santé). L’accompagnement garantit
                que vous pratiquez les bons gestes, à la bonne intensité, en toute sécurité.
              </p>
            </FaqItem>
          </div>
        </div>
      </section>

      {/* ================== 12. Urgences (144) ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="12">Sécurité</SectionLabel>
          <SectionTitle>Quand consulter et quand appeler les urgences</SectionTitle>

          <div
            className="rounded-2xl p-6 sm:p-8 mt-6 mb-8"
            style={{ backgroundColor: C.dangerBg, border: `1px solid ${C.dangerBorder}` }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ backgroundColor: C.danger, color: C.white }}
              >
                <Phone size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: C.danger, fontWeight: 600 }}>
                  ⚠ Urgence vitale
                </p>
                <p
                  className="text-2xl sm:text-3xl"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 400,
                    color: C.danger,
                  }}
                >
                  Appelez immédiatement les urgences (144) en cas de :
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ['Douleur', 'Douleur thoracique brutale, intense ou qui s’étend (bras, mâchoire, dos)'],
                ['Souffle', 'Essoufflement soudain et sévère, impossibilité de parler'],
                ['Malaise', 'Évanouissement, malaise, perte de connaissance'],
                ['Couleur', 'Lèvres, visage ou doigts bleutés (cyanose)'],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="rounded-xl p-4 flex gap-3"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.dangerBorder}` }}
                >
                  <AlertTriangle size={18} style={{ color: C.danger, flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: C.danger }}>{k}</p>
                    <p className="text-sm" style={{ color: C.ink }}>{v}</p>
                  </div>
                </div>
              ))}
            </div>
            <a
              href="tel:144"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg"
              style={{ backgroundColor: C.danger, color: C.white }}
              data-testid="cta-call-144"
            >
              <Phone size={18} />
              Appeler le 144
            </a>
          </div>

          <Callout icon={Stethoscope} title="Consultez un médecin rapidement si">
            Vos symptômes sont récents, inhabituels, ou s’aggravent ; vous avez de la
            fièvre ; une perte de poids inexpliquée ; des réveils nocturnes avec
            suffocation ; des crachats teintés de sang ; une saturation en oxygène basse ;
            ou si vous avez un terrain cardiaque, pulmonaire ou neurologique et que quelque
            chose change.
          </Callout>
          <div className="mt-4">
            <Callout icon={ClipboardList} title="À retenir">
              Le syndrome d’hyperventilation est un diagnostic qui se pose{' '}
              <strong>après</strong> vérification médicale, pas avant. La rééducation
              respiratoire ne se substitue jamais à un avis médical. Si vous n’avez pas
              encore consulté votre médecin traitant, c’est la première étape.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== Final CTA ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <div
            className="rounded-3xl p-8 sm:p-12 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              color: C.white,
            }}
          >
            <h3
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}
            >
              Prêt à retrouver un souffle calme&nbsp;?
            </h3>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              Nos exercices guidés vous accompagnent pas à pas, avec un suivi de vos
              progrès (pause de contrôle, score BOLT) et des défis pour mesurer votre évolution.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/respiration')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300 hover:-translate-y-1"
                style={{ backgroundColor: C.white, color: C.primaryDark }}
                data-testid="cta-start-program"
              >
                Démarrer le programme
                <ArrowRight size={18} />
              </button>
              <Link
                to="/evaluer-respiration"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300"
                style={{
                  backgroundColor: 'transparent',
                  color: C.white,
                  border: `1.5px solid rgba(255,255,255,0.5)`,
                }}
                data-testid="cta-evaluate"
              >
                Évaluer ma respiration
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default HyperventilationPage;
